import { DateFormatter, DateRange } from 'react-day-picker'
import { addBusinessDays, format, isWeekend, startOfDay } from 'date-fns'

export const formatWeekdayName: DateFormatter = (date, options) => format(date, 'ccc', options).toUpperCase()

export const dateValue = (date: Date) => `${format(date, 'MMM')} ${date.getDate()}`

const rangeValue = ({ from, to }: DateRange) => {
  if (from && !to) return dateValue(from)
  if (from && to) return `${dateValue(from)} - ${dateValue(to)}`
}

export const formatValue = (mode: 'single' | 'range', value?: DateRange | Date, label?: string) => {
  if (!value) return label

  if (mode === 'single') return dateValue(value as Date)

  return rangeValue(value as DateRange)
}

export type Month = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
export type Holiday = Record<number, Record<Month, number[]>>
export type BusinessHours = {
  startHour: string
  endHour: string
}

export const yearTemplate = () => ({
  1: [],
  2: [],
  3: [],
  4: [],
  5: [],
  6: [],
  7: [],
  8: [],
  9: [],
  10: [],
  11: [],
  12: [],
})

export const holidayTemplate = () => {
  const holidayTemplate: Holiday = {}
  for (let year = 2023; year <= 2099; year++) {
    holidayTemplate[year] = yearTemplate()
  }

  return holidayTemplate
}

export const isHoliday = (date: Date, buildingHolidays?: Holiday): boolean => {
  if (!buildingHolidays) return false

  const year = date.getFullYear() as keyof Holiday
  const month = (date.getMonth() + 1) as Month
  const day = date.getDate()

  try {
    return !!buildingHolidays?.[year][month].includes(day)
  } catch {
    console.error(
      'Failure checking holidays, this may happen because the year that is being checked does not have a defined list'
    )
    return false
  }
}

export const pastWorkingHours = (date: Date): boolean => date.getHours() >= 16

/**
 * Return the next valid business day (not holiday, weekend, or past 5 PM)
 * @param date The date to start checking from, should be the current time
 */
export const validBusinessDay = (date: Date, buildingHolidays?: Holiday): Date => {
  if (pastWorkingHours(date)) return validBusinessDay(addBusinessDays(startOfDay(date), 1), buildingHolidays)
  if (!isWeekend(date) && !isHoliday(date, buildingHolidays)) return date

  return validBusinessDay(addBusinessDays(date, 1), buildingHolidays)
}

export const makeBookingStartDate = (date: Date, buildingHolidays?: Holiday): Date | undefined => {
  // Check for the next valid business date but don't take into account any holidays
  const nextBusinessDay = validBusinessDay(date)
  if (isHoliday(nextBusinessDay, buildingHolidays)) {
    return undefined
  }

  return nextBusinessDay
}
